import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Download, Smartphone } from 'lucide-react';
import { Button } from './ui/button';

export default function PWAInstallPrompt() {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    try {
      // Check if already installed
      if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
        return;
      }

      // Check if iOS
      const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent);
      setIsIOS(isIOSDevice);

      // Check if dismissed recently
      const dismissedTime = localStorage.getItem('pwa-prompt-dismissed');
      if (dismissedTime) {
        const daysSinceDismissed = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60 * 24);
        if (daysSinceDismissed < 7) {
          return;
        }
      }

      // Listen for beforeinstallprompt (Android/Desktop)
      const handler = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setTimeout(() => setShowPrompt(true), 3000);
      };

      window.addEventListener('beforeinstallprompt', handler);

      return () => {
        window.removeEventListener('beforeinstallprompt', handler);
      };
    } catch (error) {
      // Silently fail on unsupported browsers
    }
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
          setShowPrompt(false);
        }
      } catch (error) {
        // Installation not supported
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    try {
      localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
    } catch (error) {
      // localStorage not available
    }
  };

  if (!showPrompt) {
    return null;
  }

  return (
    <div 
      className="fixed bottom-20 left-4 right-4 lg:bottom-4 lg:left-auto lg:right-4 lg:w-96 z-50"
      data-testid="pwa-install-prompt"
    >
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-2 rounded-xl">
                <Smartphone className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">{t('install_app')}</h3>
                <p className="text-blue-100 text-sm">{t('install_app_subtitle')}</p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="text-white/70 hover:text-white transition-colors p-1"
              data-testid="pwa-dismiss-button"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        
        <div className="p-4">
          {isIOS ? (
            <div className="space-y-3">
              <p className="text-slate-600 text-sm">{t('ios_install_instructions')}</p>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <span className="bg-slate-100 px-2 py-1 rounded">1. {t('tap_share')}</span>
                <span>→</span>
                <span className="bg-slate-100 px-2 py-1 rounded">2. {t('add_to_home')}</span>
              </div>
            </div>
          ) : (
            <Button 
              onClick={handleInstall}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="pwa-install-button"
            >
              <Download className="h-4 w-4 mr-2" />
              {t('install_now')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
